import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Users, Loader2, Check } from 'lucide-react';
import { useState } from 'react';

const PRODUCTION_DAYS = ['FRI', 'SAT', 'SUN', 'MON', 'TUES', 'WED', 'THURS'];

const ClientAllergyInput = ({ value, onChange, lineId }) => (
  <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
    placeholder="Notes..." className="text-xs h-7 w-[140px]"
    data-testid={`client-allergy-${lineId}`} />
);

const QuantityCell = ({ item, clientName, updateClientQuantity }) => {
  const [val, setVal] = useState(String(item.client_quantity));
  const [saving, setSaving] = useState(false);
  const original = String(item.client_quantity);
  const dirty = val !== original;

  const commit = async () => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 0) {
      setVal(original);
      return;
    }
    if (n === item.client_quantity) return;
    setSaving(true);
    try {
      await updateClientQuantity(item.id, clientName, n);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        min="0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setVal(original); }}
        className="w-16 h-7 text-sm tabular-nums px-2"
        data-testid={`client-qty-input-${item.id}`}
        disabled={saving}
      />
      {dirty && (
        <button
          onClick={commit}
          disabled={saving}
          className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
          title="Save quantity"
          data-testid={`client-qty-save-${item.id}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
};

export const ClientDetailModal = ({
  open, onOpenChange, data, isAdmin,
  clientAllergyEdits, setClientAllergyEdits,
  updateClientLine, updateClientQuantity,
  saveClientAllergyNotes, savingAllergy
}) => {
  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] overflow-y-auto" style={{ borderRadius: '12px' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
            {data.client.name} — Order Summary
          </DialogTitle>
        </DialogHeader>
        <div className="py-3">
          {/* Client info bar */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[var(--color-primary)]" />
              <span className="font-semibold">{data.client.name}</span>
            </div>
            {data.client.key && <span className="badge-pill badge-teal font-semibold">{data.client.key}</span>}
            <span className="badge-pill badge-gray">{data.client.size}</span>
            <span className="badge-pill badge-gray">{data.client.pack_type}</span>
            <div className="ml-auto flex items-center gap-4 text-sm">
              <div><span className="text-gray-500">Dishes:</span> <span className="font-semibold tabular-nums">{data.totalDishes}</span></div>
              <div><span className="text-gray-500">Total Items:</span> <span className="font-semibold tabular-nums">{data.totalItems}</span></div>
            </div>
          </div>

          {/* Allergy notes */}
          {data.allergyNotes.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <h4 className="text-sm font-semibold text-red-700 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Allergy & Dietary Notes
              </h4>
              {data.allergyNotes.map((n) => (
                <div key={n.client_name || n.notes} className="text-sm text-red-600">{n.notes}</div>
              ))}
            </div>
          )}

          {/* Items by delivery day */}
          {Object.entries(data.byDelivery).map(([day, items]) => (
            <div key={day} className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="badge-pill badge-teal">{day}</span>
                <span className="text-gray-400">({items.length} lines, {items.reduce((s, i) => s + i.client_quantity, 0)} items)</span>
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Dish</th>
                      <th>Size</th>
                      <th>Production Day</th>
                      <th>Allergy Notes</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className={item.source?.includes('split') ? 'bg-purple-50/50' : ''}>
                        <td className="font-medium text-sm">
                          {item.dish_name}
                          {item.is_vegetarian && <span className="ml-1 text-green-600 text-xs">(v)</span>}
                          {item.source?.includes('split') && <span className="ml-1 badge-pill bg-purple-100 text-purple-700 text-[9px]">split</span>}
                          {item.source === 'manual' && <span className="ml-1 badge-pill bg-blue-100 text-blue-700 text-[9px]">manual</span>}
                        </td>
                        <td><span className="badge-pill badge-gray text-[10px]">{item.meal_size || 'Standard'}</span></td>
                        <td>
                          {isAdmin ? (
                            <Select value={item.production_day} onValueChange={(v) => updateClientLine(item.id, 'production_day', v)}>
                              <SelectTrigger className="w-[110px] text-xs h-7"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                                {PRODUCTION_DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`badge-pill text-[10px] ${item.production_day === 'UNASSIGNED' ? 'bg-amber-100 text-amber-700' : 'badge-teal'}`}>{item.production_day}</span>
                          )}
                        </td>
                        <td>
                          {isAdmin ? (
                            <ClientAllergyInput
                              lineId={item.id}
                              value={clientAllergyEdits[item.id] || ''}
                              onChange={(v) => setClientAllergyEdits(prev => ({ ...prev, [item.id]: v }))}
                            />
                          ) : (
                            <span className="text-xs text-red-600">{item.client_allergy_notes || '-'}</span>
                          )}
                        </td>
                        <td className="tabular-nums font-semibold">
                          {isAdmin ? (
                            <QuantityCell
                              item={item}
                              clientName={data.client.name}
                              updateClientQuantity={updateClientQuantity}
                            />
                          ) : (
                            item.client_quantity
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {data.totalDishes === 0 && (
            <div className="text-center py-8 text-gray-400">No items found for this client.</div>
          )}

          {/* Save Allergy Notes Button */}
          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
              <Button onClick={saveClientAllergyNotes} disabled={savingAllergy} className="btn-primary" data-testid="save-allergy-notes-btn">
                {savingAllergy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                Save Allergy Notes
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
